import React, { FC, useEffect } from "react";
import landingImage from "../../assets/images/landing2.png";
import { useTranslation } from "react-i18next";
import { APP_CONFIG } from "../../constants/constants";
import { useNavigate } from "react-router-dom";
import { routeUrls } from "../../navigation/routeUrls";
import svgIcons from "../../assets/svg/svgIcons";
import { useSelector } from "react-redux";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import { TLegacyUser } from "../../api/types/User";
import SysadminAccountUserAccessBar from "../sysadmin/sysadminAccountUserAccessBar";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";
import { useThemeAssets } from "../common/ThemeContext";

interface LandingWrapperProps extends React.PropsWithChildren {
  children: React.ReactNode;
}

const LandingWrapper: FC<LandingWrapperProps> = ({ children }) => {
  const { t } = useTranslation("translation", { keyPrefix: "landingScreen" });
  const navigate = useNavigate();

  const { assets } = useThemeAssets();

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const loggedInUser: TLegacyUser =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  return (
    <section className="bg-white">
      {/* Blue bar - shows when sysadmin user logged in as an account user */}
      {userIsImpersonated && <SysadminAccountUserAccessBar />}

      {/* Landing Page */}
      <div className="lg:grid lg:min-h-screen lg:grid-cols-12">
        <section className="relative flex items-center bg-white lg:col-span-6 lg:h-full xl:col-span-6">
          <div className="flex flex-row lg:flex-col justify-center lg:p-12 lg:pb-0 w-full lg:w-auto">
            <div className="hidden lg:relative lg:block">
              <a
                className="block text-white cursor-pointer"
                onClick={() => navigate(routeUrls.landingPage)}
                title={APP_CONFIG.APP_NAME}
              >
                <span className="sr-only">Home</span>
                <img
                  src={assets?.logoUrl || svgIcons.Logo}
                  alt={APP_CONFIG.APP_NAME}
                  width={200}
                  height={150}
                />
              </a>

              <h1 className="mt-10 font-display text-2xl font-extralight fw text-heading-gray444 sm:text-3xl md:text-4xl">
                {assets?.landing_page_heading || t("heading")}
              </h1>

              <p className="mt-4 leading-normal text-base text-heading-gray666">
                {assets?.landing_page_content || t("content")}
              </p>
            </div>

            <img
              alt=""
              src={assets?.landingScreenImageUrl || landingImage}
              className="object-contain h-48 m-12 lg:h-48 lg:mt-6"
            />
          </div>
        </section>

        <main className="flex items-center justify-center px-8 py-8 sm:px-12 lg:col-span-6 lg:px-16 lg:py-12 xl:col-span-6 bg-panel-gray">
          <div className="w-full max-w-md lg:max-w-lg">
            <div className="relative -mt-16 mb-6 block lg:hidden">
              <a
                className="inline-flex w-60 h-16 items-center justify-center rounded-full bg-white text-blue-600 cursor-pointer"
                onClick={() => navigate(routeUrls.landingPage)}
                title={APP_CONFIG.APP_NAME}
              >
                <span className="sr-only">{APP_CONFIG.APP_NAME}</span>
                <img
                  className="h-10"
                  src={assets?.logoUrl ? assets?.logoUrl : svgIcons.Logo}
                  alt={APP_CONFIG.APP_NAME}
                />
              </a>
            </div>

            {children}

            <div className="relative block lg:hidden mt-12 mb-6">
              <h1 className="mt-2 text-2xl font-extralight fw text-heading-gray444 sm:text-3xl md:text-4xl">
                {t("heading")}
              </h1>

              <p className="mt-4 leading-relaxed text-base text-heading-gray666">
                {t("content")}
              </p>
            </div>
          </div>
        </main>
      </div>
    </section>
  );
};
export default LandingWrapper;
